import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Booking, Service, AppState } from '../types'

// Initial mock data
const initialMockBookings: Booking[] = [
  {
    id: '1',
    service_name: 'House Cleaning',
    date: '2024-03-20',
    time: '10:00 AM',
    status: 'confirmed',
    amount: 150
  },
  {
    id: '2',
    service_name: 'Office Cleaning',
    date: '2024-03-22',
    time: '2:00 PM',
    status: 'pending',
    amount: 300
  },
  {
    id: '3',
    service_name: 'Deep Cleaning',
    date: '2024-03-25',
    time: '9:00 AM',
    status: 'completed',
    amount: 250
  },
  {
    id: '4',
    service_name: 'Carpet Cleaning',
    date: '2024-03-18',
    time: '11:00 AM',
    status: 'confirmed',
    amount: 120
  },
  {
    id: '5',
    service_name: 'Window Cleaning',
    date: '2024-03-19',
    time: '3:00 PM',
    status: 'pending',
    amount: 80
  },
  {
    id: '6',
    service_name: 'Kitchen Deep Clean',
    date: '2024-03-21',
    time: '8:00 AM',
    status: 'confirmed',
    amount: 200
  },
  {
    id: '7',
    service_name: 'Bathroom Cleaning',
    date: '2024-03-17',
    time: '1:00 PM',
    status: 'completed',
    amount: 90
  },
  {
    id: '8',
    service_name: 'Post-Construction Clean',
    date: '2024-03-23',
    time: '10:00 AM',
    status: 'pending',
    amount: 450
  },
  {
    id: '9',
    service_name: 'Regular Maintenance',
    date: '2024-03-24',
    time: '2:00 PM',
    status: 'confirmed',
    amount: 175
  }
]

// Initial mock users data
const initialMockUsers: User[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    role: 'client',
    status: 'active',
    created_at: '2024-01-15',
    phone: '(555) 123-4567',
    address: '123 Main St, Downtown Apartment',
    total_jobs: 24,
    total_spent: 3600
  },
  {
    id: '2',
    name: 'Mike Williams',
    email: 'mike.w@email.com',
    role: 'provider',
    status: 'active',
    created_at: '2024-01-10',
    phone: '(555) 987-6543',
    address: '456 Oak Ave, Suburban House',
    total_jobs: 156,
    total_spent: 0,
    rating: 4.8
  },
  {
    id: '3',
    name: 'Emily Davis',
    email: 'emily.d@email.com',
    role: 'client',
    status: 'active',
    created_at: '2024-02-01',
    phone: '(555) 456-7890',
    address: '789 Business Center Blvd',
    total_jobs: 8,
    total_spent: 1200
  },
  {
    id: '4',
    name: 'Robert Brown',
    email: 'robert.b@email.com',
    role: 'provider',
    status: 'active',
    created_at: '2024-01-20',
    phone: '(555) 234-5678',
    address: '321 Residential Complex',
    total_jobs: 89,
    total_spent: 0,
    rating: 4.9
  },
  {
    id: '5',
    name: 'Lisa Anderson',
    email: 'lisa.a@email.com',
    role: 'client',
    status: 'inactive',
    created_at: '2024-02-15',
    phone: '(555) 345-6789',
    address: '555 Elm Street',
    total_jobs: 3,
    total_spent: 450
  }
]

// Initial mock services data
const initialMockServices: Service[] = [
  {
    id: '1',
    name: 'Standard House Cleaning',
    description: 'Complete cleaning of all rooms including dusting, vacuuming, mopping, and bathroom sanitization',
    category: 'Residential',
    base_price: 150,
    duration: 3,
    status: 'active',
    created_at: '2024-01-01'
  },
  {
    id: '2',
    name: 'Deep Kitchen Cleaning',
    description: 'Intensive cleaning of kitchen including cabinets, appliances, countertops, and floors',
    category: 'Residential',
    base_price: 200,
    duration: 4,
    status: 'active',
    created_at: '2024-01-01'
  },
  {
    id: '3',
    name: 'Office Cleaning',
    description: 'Professional cleaning of office spaces including workspaces, common areas, and restrooms',
    category: 'Commercial',
    base_price: 300,
    duration: 5,
    status: 'active',
    created_at: '2024-01-01'
  },
  {
    id: '4',
    name: 'Carpet Shampooing',
    description: 'Deep cleaning of carpets using professional equipment and cleaning solutions',
    category: 'Specialized',
    base_price: 120,
    duration: 2,
    status: 'active',
    created_at: '2024-01-01'
  },
  {
    id: '5',
    name: 'Window Cleaning',
    description: 'Interior and exterior window cleaning for residential and commercial properties',
    category: 'Specialized',
    base_price: 80,
    duration: 2,
    status: 'inactive',
    created_at: '2024-01-01'
  },
  {
    id: '6',
    name: 'Post-Construction Cleaning',
    description: 'Thorough cleaning after construction or renovation projects',
    category: 'Commercial',
    base_price: 450,
    duration: 8,
    status: 'active',
    created_at: '2024-01-01'
  }
]

export const useAppStore = create<AppState>()(
  persist(
    (set: (state: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void, get: () => AppState) => ({
      user: null,
      bookings: [], // Start with empty bookings for new users
      users: initialMockUsers,
      services: initialMockServices,
      loading: true, // Start with loading true during hydration
      
      // User actions
      setUser: (user: User | null) => set((state) => {
        console.log('Store setUser called with:', user)
        console.log('Store previous user:', state.user)
        
        // Clear any old mock data for real users
        if (user) {
          const demoEmails = ['sarah.j@email.com', 'mike.w@email.com', 'emily.d@email.com', 'robert.b@email.com', 'lisa.a@email.com'];
          
          if (demoEmails.includes(user.email)) {
            // Only populate demo data for demo accounts if empty
            if (state.bookings.length === 0) {
              state.setBookings(initialMockBookings);
            }
          } else {
            // Clear any old mock data for real users
            if (state.bookings.some(booking => booking.date.startsWith('2024'))) {
              state.setBookings([]);
            }
          }
        }
        
        console.log('Store new user being set:', user)
        return { user };
      }),
      
      // Booking CRUD actions
      setBookings: (bookings: Booking[]) => set({ bookings }),
      addBooking: (booking: Booking) => set((state) => ({ 
        bookings: [...state.bookings, booking] 
      })),
      updateBooking: (id: string, updates: Partial<Booking>) => set((state) => ({
        bookings: state.bookings.map((booking: Booking) => 
          booking.id === id ? { ...booking, ...updates } : booking
        )
      })),
      deleteBooking: (id: string) => set((state) => ({
        bookings: state.bookings.filter((booking: Booking) => booking.id !== id)
      })),
      
      // User CRUD actions
      setUsers: (users: User[]) => set({ users }),
      addUser: (user: User) => set((state) => ({ 
        users: [...state.users, user] 
      })),
      updateUser: (id: string, updates: Partial<User>) => set((state) => ({
        users: state.users.map((user: User) => 
          user.id === id ? { ...user, ...updates } : user
        )
      })),
      deleteUser: (id: string) => set((state) => ({
        users: state.users.filter((user: User) => user.id !== id)
      })),
      
      // Service CRUD actions
      setServices: (services: Service[]) => set({ services }),
      addService: (service: Service) => set((state) => ({ 
        services: [...state.services, service] 
      })),
      updateService: (id: string, updates: Partial<Service>) => set((state) => ({
        services: state.services.map((service: Service) => 
          service.id === id ? { ...service, ...updates } : service
        )
      })),
      deleteService: (id: string) => set((state) => ({
        services: state.services.filter((service: Service) => service.id !== id)
      })),
      
      setLoading: (loading: boolean) => set({ loading }),
      
      // Filtered data getters for real admins
      getRealUsers: () => {
        const state = get()
        const demoEmails = ['sarah.j@email.com', 'mike.w@email.com', 'emily.d@email.com', 'robert.b@email.com', 'lisa.a@email.com', 'admin@demo.com', 'test@admin.com'];
        return state.users.filter(user => !demoEmails.includes(user.email));
      },
      
      getRealBookings: () => {
        const state = get()
        return state.bookings.filter(booking => !booking.date.startsWith('2024'));
      },
      
      getRealServices: () => {
        const state = get()
        return state.services; // Services are real, no demo services in current setup
      },
    }),
    {
      name: 'smartclean-storage-v2', // New name to force fresh start
      partialize: (state) => ({ 
        user: state.user,
        bookings: state.bookings,
        users: state.users,
        services: state.services
      }),
      onRehydrateStorage: () => (state) => {
        // Set loading to false when hydration is complete
        if (state) {
          state.setLoading(false)
          
          // Clear any old mock data on rehydration
          if (state.bookings.some(booking => booking.date.startsWith('2024'))) {
            state.setBookings([])
          }
        }
      }
    }
  )
)
